import { useState, useCallback, useEffect } from "react";
import bookIcon from "../../assets/images/icons/book.svg";
import familyIcon from "../../assets/images/icons/family.svg";
import graduationIcon from "../../assets/images/icons/graduation.svg";
import aiIcon from "../../assets/images/icons/ai.svg";
import appleIcon from "../../assets/images/icons/apple.svg";
import androidIcon from "../../assets/images/icons/android.svg";
import rocketImage from "../../assets/images/icons/rocket.svg";
import playMonitorImage from "../../assets/images/icons/play_monitor.svg";
import bannerImage1 from "../../assets/images/banner1.png";
import bannerImage2 from "../../assets/images/banner2.png";

import "../../assets/css/app_landing.scss";
import Header from "../../components/header";
import Footer from "../../components/footer";
import NewsDetailModal from "../../components/modal/news_detail_modal";
import { useNavigate } from "react-router-dom";
import { getGeneralBoardList } from "../../api/board";
import { parseContent } from "../../utils/quill_parser_util";
import BannerSlider from "../../components/banner_slider";

function AppLanding() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("전체");
  const [selectedNews, setSelectedNews] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newsData, setNewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const banners = [
    {
      url: "https://kids.donga.com/news/articleView.html?idxno=167532",
      image: bannerImage1,
      alt: "11월호 도전 설문 참여하고 선물 받자!",
    },
    {
      url: "https://edu.donga.com/news/articleView.html?idxno=100482",
      image: bannerImage2,
      alt: "SK텔레콤 2026 zem폰 포켓피스 구매 고객 이번트!",
    },
  ];

  const getTabType = (tab) => {
    const tabMapping = {
      전체: "ALL",
      공지사항: "NOTICE",
      이벤트: "NOTICE_EVENT",
      FAQ: "FAQ",
    };
    return tabMapping[tab] || "ALL";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  };

  const transformBoardData = (boardList) => {
    return boardList.map((item) => {
      const parsedContent = parseContent(item.content || "");

      let description = "";
      if (item.content) {
        description = parseContent(item.content);
      } else {
        description = parsedContent.substring(0, 300);
        if (parsedContent.length > 300) {
          description += "...";
        }
      }

      let extractedImage = null;
      let descriptionWithoutImage = description;

      const imgRegex = /<img[^>]+src=["']([^"'>]+)["']/i;
      const imgMatch = description.match(imgRegex);

      if (imgMatch && imgMatch[1]) {
        extractedImage = imgMatch[1];

        descriptionWithoutImage = description
          .replace(/<img[^>]*>/gi, "")
          .trim();
      }

      return {
        id: item.boardSeq,
        badge: item.detailType == "0032" ? "이벤트" : item.typeName,
        title: item.title || "",
        desc: descriptionWithoutImage || "",
        fullDesc: parsedContent,
        date: formatDate(item.regDate),
        image: extractedImage,
        originalData: item,
      };
    });
  };

  const fetchNewsList = useCallback(async () => {
    setIsLoading(true);
    try {
      const type = getTabType(activeTab);
      const result = await getGeneralBoardList(type, 0, 12);

      if (result.success && result.data) {
        const transformedData = transformBoardData(result.data);
        setNewsData(transformedData);
      } else {
        console.error("Failed to fetch news list:", result.error);

        setNewsData([]);
      }
    } catch (error) {
      console.error("Error fetching news list:", error);
      setNewsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchNewsList();
  }, [fetchNewsList]);

  const applyMasonryLayout = useCallback(() => {
    const container = document.querySelector(".news-grid");
    if (!container) return;

    if (window.innerWidth <= 768) {
      container.style.height = "auto";
      Array.from(container.children).forEach((child) => {
        child.style.position = "relative";
        child.style.top = "auto";
        child.style.left = "auto";
      });
      return;
    }

    const numCols = window.innerWidth <= 1024 ? 2 : 3;
    const cards = Array.from(container.children);
    const gap = 24;
    const containerWidth = container.offsetWidth;
    const cardWidth = (containerWidth - gap * (numCols - 1)) / numCols;

    const colHeights = Array(numCols).fill(0);

    cards.forEach((card) => {
      const minColIndex = colHeights.indexOf(Math.min(...colHeights));

      card.style.position = "absolute";
      card.style.width = `${cardWidth}px`;
      card.style.left = `${minColIndex * (cardWidth + gap)}px`;
      card.style.top = `${colHeights[minColIndex]}px`;

      colHeights[minColIndex] += card.offsetHeight + gap;
    });

    container.style.height = `${Math.max(...colHeights)}px`;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      applyMasonryLayout();
    }, 100);

    const recalcTimer = setTimeout(() => {
      applyMasonryLayout();
    }, 300);

    const finalTimer = setTimeout(() => {
      applyMasonryLayout();
    }, 600);

    const handleResize = () => {
      applyMasonryLayout();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      clearTimeout(recalcTimer);
      clearTimeout(finalTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [applyMasonryLayout, activeTab, newsData]);

  const handleNewsClick = (news) => {
    setSelectedNews(news);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedNews(null);
    }, 300);
  };

  return (
    <div className="app-landing-renewed">
      <Header />

      <section className="hero-section" id="intro">
        <div className="hero-container">
          <div className="hero-badge">
            <span>
              <img src={aiIcon} alt="AI" />
            </span>{" "}
            AI와 함께하는 어린이 맞춤 서비스
          </div>
          <h1 className="hero-title">새로워진 어린이 뉴스앱, 키즈다</h1>
          <p className="hero-subtitle">
            AI와 함께 매일 새로운 시사뉴스와 학습 콘텐츠를 즐겨보세요
          </p>
          <div className="download-buttons">
            <a
              href="https://apps.apple.com/kr/app/키즈다/id6738370393"
              target="_blank"
              rel="noreferrer"
              className="download-btn ios"
            >
              <span className="icon">
                <img src={appleIcon} alt="Apple" />
              </span>{" "}
              iOS 앱 다운로드
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.aslo.smartview.donga.kids"
              target="_blank"
              rel="noreferrer"
              className="download-btn android"
            >
              <span className="icon">
                <img src={androidIcon} alt="Android" />
              </span>{" "}
              Android 앱 다운로드
            </a>
          </div>
        </div>
      </section>

      <section className="features-section" id="features">
        <div className="features-container">
          <div className="features-grid">
            <div className="feature-card">
              <div className="card-icon-wrapper">
                <img src={bookIcon} alt="교과학습" />
              </div>
              <h3>교과와 연결된 시사뉴스</h3>
              <p>
                국어·수학·사회·과학과 연결된 시사 뉴스를 매 일 큐레이션합니다.
                요약과 퀴즈로 자연스럽게 복습까지 이어집니다.
              </p>
            </div>
            <div className="feature-card">
              <div className="card-icon-wrapper">
                <img src={familyIcon} alt="부모와 함께" />
              </div>
              <h3>부모와 함께하는 학습 관리</h3>
              <p>
                부모 계정과 연동해 아이가 읽은 콘텐츠와 학 습 리포트를 한눈에
                확인하고 함께 읽기 기능 으로 대화를 이어가세요.
              </p>
            </div>
            <div className="feature-card">
              <div className="card-icon-wrapper">
                <img src={graduationIcon} alt="학습도움" />
              </div>
              <h3>학부모를 위한 교육 정보</h3>
              <p>
                최신 교육 정책과 입시 브리핑, 진로 탐색 가이 드를 전문가
                칼럼으로 전달하며 학부모의 궁금 증을 빠르게 해결합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="platform-section">
        <div className="platform-container">
          <h2 className="section-title">플랫폼 안내</h2>
          <div className="platform-grid">
            <div className="platform-mobile">
              <div className="platform-card">
                <div className="side-card-icon">
                  <span className="emoji-computer">
                    <img src={rocketImage} alt="Rocket" />
                  </span>
                </div>
                <div className="contents">
                  <h3 className="main-card-title">
                    스터디 & 라이프 플랫폼, 키즈다
                  </h3>
                  <p className="side-card-desc">
                    아이에게는 학습 놀이터, 부모에게는 교육 파트너
                  </p>
                  <button
                    className="side-card-btn"
                    onClick={() => {
                      navigate("/product");
                    }}
                  >
                    바로가기
                  </button>
                </div>
              </div>

              <div className="platform-card">
                <div className="side-card-icon">
                  <span className="emoji-computer">
                    <img src={playMonitorImage} alt="Play Monitor" />
                  </span>
                </div>
                <div className="contents">
                  <h3 className="main-card-title">키즈다 에듀 클립</h3>
                  <p className="side-card-desc">
                    수학·영어 경시대회 학습 특강!
                  </p>
                  <button
                    className="side-card-btn"
                    onClick={() => {
                      navigate("/educlip");
                    }}
                  >
                    바로가기
                  </button>
                </div>
              </div>
            </div>

            <div className="platform-pc">
              <div className="platform-card">
                <div className="contents">
                  <h3 className="main-card-title">
                    스터디 & 라이프 플랫폼, 키즈다
                  </h3>
                  <p className="side-card-desc">
                    아이에게는 학습 놀이터, 부모에게는 교육 파트너
                  </p>
                  <button
                    className="side-card-btn"
                    onClick={() => {
                      navigate("/product");
                    }}
                  >
                    바로가기
                  </button>
                </div>
                <div className="side-card-icon">
                  <span className="emoji-computer">
                    <img src={rocketImage} alt="Rocket" />
                  </span>
                </div>
              </div>

              <div className="platform-card">
                <div className="contents">
                  <h3 className="main-card-title">키즈다 에듀 클립</h3>
                  <p className="side-card-desc">
                    수학·영어 경시대회 학습 특강!
                  </p>
                  <button
                    className="side-card-btn"
                    onClick={() => {
                      navigate("/educlip");
                    }}
                  >
                    바로가기
                  </button>
                </div>
                <div className="side-card-icon">
                  <span className="emoji-computer">
                    <img src={playMonitorImage} alt="Play Monitor" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <BannerSlider banners={banners} autoSlideInterval={5000} />
        </div>
      </section>

      <section className="news-section" id="news">
        <div className="news-container">
          <h2 className="section-title">키즈다 소식</h2>
          <div className="news-tabs">
            {["전체", "공지사항", "이벤트", "FAQ"].map((tab) => (
              <button
                key={tab}
                className={`tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="news-grid-wrapper">
            {isLoading ? (
              <div className="loading-state">
                <p>소식을 불러오는 중...</p>
              </div>
            ) : newsData.length > 0 ? (
              <div className="news-grid">
                {newsData.map((item) => (
                  <div className="notice-card" key={item.id}>
                    <p className="notice-badge">
                      <span>{item.badge}</span>
                    </p>
                    <div className="notice-date">{item.date}</div>
                    <h3 className="notice-title">{item.title}</h3>
                    {item.image && (
                      <div className="notice-image">
                        <img src={item.image} alt={item.title} />
                      </div>
                    )}
                    <p
                      className="notice-desc"
                      dangerouslySetInnerHTML={{ __html: item.desc }}
                    ></p>
                    <a
                      className="notice-link"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNewsClick(item);
                      }}
                    >
                      자세히 보기 →
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>등록된 소식이 없습니다.</p>
              </div>
            )}
            <div className="news-grid-bg"></div>
          </div>
          <button
            className="load-more-btn"
            onClick={() => {
              navigate("/news");
            }}
          >
            전체 소식 보기
          </button>
        </div>
      </section>

      <Footer />

      {isModalOpen && selectedNews && (
        <NewsDetailModal news={selectedNews} onClose={handleCloseModal} />
      )}
    </div>
  );
}

export default AppLanding;
